import React from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

import Text from '../../ui/Text'

const SButton = styled.button<{ small?: boolean; danger?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ danger, theme }) => (danger ? theme.brandColors.CA_Coral : theme.font.darkestColor)};
    color: #ffffff;
    border: none;
    font-weight: 500;
    padding: ${({ small }) => (small ? '5px 12px' : '9px 30px')};
    line-height: 1.5em;
    border-radius: 16px;
    text-transform: capitalize;
    transition: all 0.2s ease;

    &:hover {
        color: ${({ theme, danger }) => (danger ? theme.font.lightestColor : theme.font.darkestColor)};
        background-color: ${({ theme, danger }) => (danger ? theme.font.errorColor : theme.font.successColor)};
    }

    &:disabled {
        color: #ffffff;
        cursor: not-allowed;
        opacity: 0.65;
    }

    &:active {
        color: ${({ theme, danger }) => (danger ? theme.font.lightestColor : theme.font.darkestColor)};
        background-color: ${({ theme, danger }) => (danger ? theme.font.errorColor : theme.font.successColorLight)};
    }

    &[disabled] {
        background-color: ${({ theme, danger }) =>
            danger ? theme.checkbox.disabledBgColor : theme.checkbox.disabledBgColor};
        color: ${({ theme }) => theme.font.lightestColor};
    }

    &.btn-xs {
        border-radius: 3px;
        padding: 1px 5px;
        height: 23px;
        width: 21px;
    }

    &.inverted {
        background-color: ${({ theme }) => theme.whiteBackground};
        color: ${({ theme }) => theme.font.darkestColor};

        &:hover {
            opacity: 0.6;

            &:active {
                color: ${({ theme }) => theme.font.midLightColor};
                opacity: 1;
            }
        }
    }
`

const SIconContainer = styled.div<{ loadingIconPosition: LoadingIconPosition }>`
    padding-left: ${({ loadingIconPosition }) => (loadingIconPosition === LoadingIconPosition.LEFT ? '0px' : '4px')};
    padding-right: ${({ loadingIconPosition }) => (loadingIconPosition === LoadingIconPosition.RIGHT ? '0px' : '4px')};
    order: ${({ loadingIconPosition }) => (loadingIconPosition === LoadingIconPosition.LEFT ? 0 : 1)};
`

export enum LoadingIconPosition {
    RIGHT = 'right',
    LEFT = 'left',
}

export type ButtonProps = {
    loadingIconPosition?: LoadingIconPosition
    type?: 'button' | 'reset' | 'submit'
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
    disabled?: boolean
    loading?: boolean
    title?: string | JSX.Element
    tooltip?: string
    className?: ClassName
    style?: React.CSSProperties
    small?: boolean
    danger?: boolean
    dataTestId?: string
    code?: string
    icon?: React.ReactNode
}

export function Button({
    type,
    onClick,
    disabled,
    loading,
    title,
    tooltip,
    danger,
    className,
    style,
    small,
    loadingIconPosition = LoadingIconPosition.LEFT,
    dataTestId,
    code,
    icon,
}: ButtonProps): React.ReactElement {
    return (
        <SButton
            type={type}
            className={cn(className)}
            style={style}
            disabled={disabled}
            small={small}
            onClick={onClick}
            danger={danger}
            title={tooltip}
            data-testid={dataTestId}
        >
            {loading && (
                <SIconContainer loadingIconPosition={loadingIconPosition} data-testid="container">
                    <FontAwesomeIcon icon={faSpinner} pulse data-testid="spinner" />
                </SIconContainer>
            )}
            {icon}
            {title}
            {code && <Text code={code} />}
        </SButton>
    )
}
